import type { AuthUser } from "@/store/auth";
import type { Order } from "../types";

export interface TodoTag {
  key: string;
  label: string;
  className: string;
  title?: string;
}

const CHIP_BASE =
  "text-[10px] px-2 py-0.5 rounded border font-semibold whitespace-nowrap";

const PALETTE = {
  amber:
    "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 border-amber-200 dark:border-amber-800",
  violet:
    "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300 border-violet-200 dark:border-violet-800",
  red:
    "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300 border-red-200 dark:border-red-800",
  blue:
    "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 border-blue-200 dark:border-blue-800",
  emerald:
    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800",
} as const;

function chip(color: keyof typeof PALETTE): string {
  return `${CHIP_BASE} ${PALETTE[color]}`;
}

function plural(n: number, sing: string, few: string, many: string): string {
  if (n === 1) return sing;
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

export function orderTodos(
  order: Order,
  user: AuthUser | null | undefined,
): TodoTag[] {
  if (!user) return [];
  const role = user.role;
  const tags: TodoTag[] = [];
  const isGK = role === "director" || role === "coordinator";

  // „Coś nowego" od ostatniego wejścia w to zamówienie — pierwszy znacznik, bo to on
  // odpowiada na pytanie „czy muszę tu w ogóle zaglądać". Gaśnie po otwarciu szczegółów.
  if (order.has_new_activity) {
    const what = order.last_activity_label || "Nowa aktywność";
    const when = order.last_activity_at
      ? new Date(order.last_activity_at).toLocaleString("pl-PL", {
          day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
        })
      : "";
    tags.push({
      key: "new",
      label: "Nowe",
      className: chip("emerald"),
      title: when ? `${what} — ${when}` : what,
    });
  }

  if (isGK && order.pending_change_requests_count > 0) {
    const n = order.pending_change_requests_count;
    tags.push({
      key: "change",
      label: `Zmiana zamówienia (${n})`,
      className: chip("amber"),
      title: `${n} ${plural(n, "wniosek o zmianę oczekuje", "wnioski o zmianę oczekują", "wniosków o zmianę oczekuje")} na akceptację`,
    });
  }

  if (isGK && order.pending_pock_requests_count > 0) {
    const n = order.pending_pock_requests_count;
    tags.push({
      key: "pock",
      label: `POCK (${n})`,
      className: chip("violet"),
      title: `${n} ${plural(n, "wniosek o POCK oczekuje", "wnioski o POCK oczekują", "wniosków o POCK oczekuje")} na akceptację`,
    });
  }

  if (role === "contractor") {
    if (order.status === "sent") {
      tags.push({
        key: "accept",
        label: "Do zaakceptowania",
        className: chip("blue"),
        title: "Zamówienie czeka na Twoją akceptację",
      });
    } else if (order.status === "accepted" && !order.contractor_acknowledged) {
      tags.push({
        key: "ack",
        label: "Potwierdź odbiór",
        className: chip("blue"),
        title: "Potwierdź odbiór automatycznie przyjętego zamówienia",
      });
    }
  }

  if (order.unread_comments_count > 0) {
    const n = order.unread_comments_count;
    tags.push({
      key: "comments",
      label: `Komentarze (${n})`,
      className: chip("red"),
      title: `${n} ${plural(n, "nieodczytany komentarz", "nieodczytane komentarze", "nieodczytanych komentarzy")}`,
    });
  }

  return tags;
}
